import React, { useState } from "react";
import { coreCompetencies, toolsExpertise, certifications } from "../data/mock";
import { Award, Code, Users, TrendingUp } from "lucide-react";

const Skills = () => {
  const [activeTab, setActiveTab] = useState("competencies");

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Strategic Leadership":
        return <Users size={24} className="text-black" />;
      case "Technical Expertise":
        return <Code size={24} className="text-black" />;
      case "Customer Experience":
        return <TrendingUp size={24} className="text-black" />;
      default:
        return <Award size={24} className="text-black" />;
    }
  };

  const tabs = [
    { id: "competencies", label: "Core Competencies" },
    { id: "tools", label: "Tools & Technologies" },
    { id: "certifications", label: "Certifications" }
  ];

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="text-center mb-16">
        <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-black mb-6">
          Skills & Expertise
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
          A comprehensive blend of strategic vision, technical proficiency, and leadership capabilities 
          developed through years of driving digital transformation initiatives.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center mb-12">
        <div className="bg-gray-100 p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-normal tracking-wide rounded-md transition-all duration-300 ${
                activeTab === tab.id
                  ? "bg-white text-black shadow-sm"
                  : "text-gray-600 hover:text-black"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="min-h-[400px]">
        {activeTab === "competencies" && (
          <div className="grid md:grid-cols-2 gap-8">
            {coreCompetencies.map((competency, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-black group-hover:text-white transition-colors duration-300">
                    {getCategoryIcon(competency.category)}
                  </div>
                  <h3 className="text-xl font-light text-black tracking-wide">
                    {competency.category}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {competency.skills.map((skill, skillIndex) => (
                    <li 
                      key={skillIndex}
                      className="text-gray-600 font-light leading-relaxed hover:text-black transition-colors duration-300 cursor-default"
                    >
                      • {skill}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {activeTab === "tools" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {toolsExpertise.map((category, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <h3 className="text-lg font-light text-black mb-4 tracking-wide border-b border-gray-100 pb-2">
                  {category.category}
                </h3>
                <ul className="space-y-2">
                  {category.tools.map((tool, toolIndex) => (
                    <li 
                      key={toolIndex}
                      className="text-sm text-gray-600 font-light hover:text-black transition-colors duration-300 cursor-default"
                    >
                      {tool}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {activeTab === "certifications" && (
          <div className="max-w-4xl mx-auto">
            {/* Achievement Note */}
            <div className="bg-gray-50 border-l-4 border-gray-300 p-6 mb-8">
              <p className="text-gray-600 font-light text-sm leading-relaxed">
                <strong>Note:</strong> The following certifications represent completed training and demonstrated competency. 
                Some certifications may require renewal for current active status, but the knowledge and skills gained remain applicable to ongoing work.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {certifications.map((cert, index) => (
                <div 
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Award 
                        size={20} 
                        className="text-black group-hover:scale-110 transition-transform duration-300" 
                      />
                      <span className="text-gray-700 font-light tracking-wide">
                        {cert}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 font-light">
                      Completed
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Professional Development Section */}
            <div className="mt-12 text-center">
              <h3 className="text-2xl font-light text-black mb-4 tracking-wide">
                Continuous Learning Journey
              </h3>
              <p className="text-gray-600 font-light leading-relaxed max-w-2xl mx-auto">
                Professional development is an ongoing commitment. These completed certifications demonstrate 
                a foundation of knowledge that continues to evolve through practical application and staying 
                current with emerging technologies, supporting digital transformation, tools and the AI ecosystems.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Skills;